import * as THREE from 'three';

const damp = (current: number, target: number, smoothing: number, delta: number): number =>
  THREE.MathUtils.lerp(current, target, 1 - Math.exp(-smoothing * delta));

export class ShowcaseCamera {
  private yaw = -0.08;
  private pitch = 0.63;
  private distance = 17.5;
  private readonly target = new THREE.Vector3(0, 0.9, 0);
  private desiredYaw = this.yaw;
  private desiredPitch = this.pitch;
  private desiredDistance = this.distance;
  private readonly desiredTarget = this.target.clone();
  private viewportScale = 1;
  private orbitPointer: number | null = null;
  private lastX = 0;
  private lastY = 0;
  private shake = 0;

  constructor(private readonly camera: THREE.PerspectiveCamera) {
    this.update(1);
  }

  beginOrbit(pointerId: number, x: number, y: number): void {
    this.orbitPointer = pointerId;
    this.lastX = x;
    this.lastY = y;
  }

  moveOrbit(pointerId: number, x: number, y: number): void {
    if (this.orbitPointer !== pointerId) return;
    const dx = x - this.lastX;
    const dy = y - this.lastY;
    this.lastX = x;
    this.lastY = y;
    this.desiredYaw -= dx * 0.005;
    this.desiredPitch = THREE.MathUtils.clamp(this.desiredPitch + dy * 0.004, 0.28, 1.38);
  }

  endOrbit(pointerId?: number): void {
    if (pointerId !== undefined && this.orbitPointer !== pointerId) return;
    this.orbitPointer = null;
  }

  zoom(deltaY: number): void {
    this.desiredDistance = THREE.MathUtils.clamp(
      this.desiredDistance * Math.exp(deltaY * 0.0012),
      8.2,
      24,
    );
  }

  setViewportAspect(aspect: number): void {
    if (!Number.isFinite(aspect) || aspect <= 0) return;
    // PerspectiveCamera's vertical FOV otherwise crops both console controls on
    // portrait touch screens. Preserve the same composition by backing away as
    // horizontal field of view narrows.
    this.viewportScale = THREE.MathUtils.clamp(1.18 / aspect, 1, 2.7);
  }

  focusConsole(consolePosition: THREE.Vector3, playing: boolean): void {
    this.desiredTarget.copy(consolePosition).add(new THREE.Vector3(0, 0.45, 0));
    this.desiredDistance = playing ? 11.2 : 14.6;
    this.desiredYaw = playing ? -0.015 : -0.08;
    // A near screen-normal gameplay view preserves the LCD's native 3:2 shape.
    this.desiredPitch = playing ? 1.22 : 0.9;
  }

  reset(): void {
    this.desiredTarget.set(0, 0.9, 0);
    this.desiredDistance = 17.5;
    this.desiredYaw = -0.08;
    this.desiredPitch = 0.63;
  }

  impulse(amount: number): void {
    this.shake = Math.max(this.shake, amount);
  }

  update(delta: number): void {
    this.yaw = damp(this.yaw, this.desiredYaw, 6.5, delta);
    this.pitch = damp(this.pitch, this.desiredPitch, 6.5, delta);
    this.distance = damp(this.distance, this.desiredDistance, 5.2, delta);
    this.target.lerp(this.desiredTarget, 1 - Math.exp(-5.5 * delta));

    const framedDistance = this.distance * this.viewportScale;
    const horizontal = Math.cos(this.pitch) * framedDistance;
    const jitter = this.shake > 0.001 ? (Math.random() - 0.5) * this.shake : 0;
    this.camera.position.set(
      this.target.x + Math.sin(this.yaw) * horizontal + jitter,
      this.target.y + Math.sin(this.pitch) * framedDistance + Math.abs(jitter),
      this.target.z + Math.cos(this.yaw) * horizontal + jitter,
    );
    this.camera.lookAt(this.target);
    this.shake *= Math.exp(-12 * delta);
  }
}
